import React, { useEffect, useMemo, useState } from "react";
import RfmHistoryModal from "./modals/RfmHistoryModal";
import '../css/card.css'
import '../css/table-discounts.css'

const tableSessionLabel = (session) => {
  const status = String(session?.status || "").toUpperCase();
  if (!session) return "";
  if (["PENDING", "REQUESTED", "PENDIENTE"].includes(status)) return `Solicitud ${session.source || "QR"} pendiente`;
  if (session.has_active_order) return "Pedido e-commerce activo";
  return "Cliente e-commerce autorizado";
};

export default function RfmListView({ tables = [], zones = [], statuses = [], onSelectTable, onEventsSeen, onChargePosPayment, onReviewDiscounts }) {
  const zoneById = useMemo(() => {
    const m = new Map();
    zones.forEach((z) => m.set(z.id, z));
    return m;
  }, [zones]);

  const statusById = useMemo(() => {
    const m = new Map();
    statuses.forEach((s) => m.set(s.id, s));
    return m;
  }, [statuses]);

  const [showHistory, setShowHistory] = useState(false);
  // guarda el id de la mesa cuyo historial se está viendo; la mesa se resuelve desde `tables`
  // para que el modal abierto reciba los eventos nuevos en vivo.
  const [activeTableId, setActiveTableId] = useState(null);
  const activeTable = useMemo(
    () => tables.find((t) => t.id === activeTableId) || null,
    [tables, activeTableId]
  );

  // Con el historial abierto todo lo que llegue ya se está viendo: no enciende el punto rojo.
  const activeEventsCount = activeTable?.events?.length || 0;
  useEffect(() => {
    if (showHistory && activeTableId) onEventsSeen?.(activeTableId);
  }, [showHistory, activeTableId, activeEventsCount]);

  return (
    <div>
      <div className="cds-x-scope cds-x-grid mt-2">
        {tables.map((t) => {
          const zone = zoneById.get(t.zone);
          const status = statusById.get(t.status);
          const remaining = (t.total || 0) - (t.paid || 0);

          // último evento del historial de ESTA mesa (el backend lo guarda mientras dura la sesión)
          const events = Array.isArray(t.events) ? t.events : [];
          const last = events.length ? events[events.length - 1] : null;
          const lastEvent = last
            ? { label: last.title, time: last.time, unseen: Boolean(t.historyUnseen) }
            : null;

          return (
            <div
              key={t.id}
              className={`cds-x-card${t.hasPendingAlert ? " has-alert" : ""}`}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter") onSelectTable?.(t.id);
              }}

              onClick={(e) => {
                e.stopPropagation();
                onSelectTable?.(t.id);

              }}
            >
              {/* nombre + zona */}
              <div className="cds-x-head">
                <div className="cds-x-name">
                  {t.hasPendingAlert ? (
                    <span className="cds-x-alert-dot" title="Actividad nueva sin revisar" />
                  ) : null}
                  Mesa {t.number}
                </div>
              </div>

              {/* sillas + mesero + zona */}
              <div className="cds-x-meta">
                {t.chairs} sillas{t.waiter_name ? ` · ${t.waiter_name}` : ""} /
                <span className="cds-x-zone">
                  <span className="cds-x-zone-dot" style={{ background: zone?.color || "#999" }} />
                  {zone?.name || ""}
                </span>
              </div>

              {/* badges de sesión e-commerce */}
              {t.tableSession ? (
                <div className="cds-x-session">{tableSessionLabel(t.tableSession)}</div>
              ) : null}
              {t.tableSession && Number(t.tableSession.participant_count) > 1 ? (
                <div className="cds-x-session is-participants" title="Participantes en la sesion e-commerce">
                  <i className="bi bi-people-fill" /> {t.tableSession.participant_count} participantes en ecommerce
                </div>
              ) : null}

              {/* estado + total */}
              <div className="cds-x-row">
                <span className={`cds-x-status ${t.status}`}>
                  {status?.name || ""}
                </span>
                <span className={`cds-x-total ${remaining > 0 ? "" : "is-empty"}`}>
                  {remaining > 0 ? `$${remaining.toFixed(2)}` : "Sin cuenta"}
                </span>
              </div>

              {/* acciones */}
              <div className="cds-x-actions">
                <div className="cds-x-actions-top">
                  {/* Ver detalle (sin dot de historial) */}
                  <button
                    className="cds-x-btn cds-x-btn-hist"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectTable?.(t.id);
                    }}
                  >
                    <span className="lbl">Ver detalle</span>
                    <span className="prev">Pedidos</span>
                  </button>

                  {/* Historial (con preview + dot) */}
                  <button
                    className="cds-x-btn cds-x-btn-hist"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveTableId(t.id);
                      setShowHistory(true);
                    }}
                  >
                    <span className="lbl">
                      Actividad
                      {lastEvent?.unseen ? <span className="cds-x-hist-dot pulse" title="Actividad nueva" /> : null}
                    </span>
                    {/* <span className="prev">
                      {lastEvent
                        ? `${lastEvent.label}${lastEvent.time ? ` · ${lastEvent.time}` : ""}`
                        : "Sin actividad"}
                    </span> */}

                    <span className={`prev${(lastEvent && `${lastEvent.label} · ${lastEvent.time}`.length > 28) ? " is-marquee" : ""}`}>
                      <span>
                        {lastEvent
                          ? `${lastEvent.label}${lastEvent.time ? ` · ${lastEvent.time}` : ""}`
                          : "Sin actividad"}
                      </span>
                    </span>
                  </button>
                </div>

                {/* El cliente pidió un descuento sobre un producto: se revisa/aprueba desde aquí. */}
                {t.pendingDiscountCount > 0 ? (
                  <button
                    className="cds-x-btn cds-x-btn-discount"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onReviewDiscounts?.(t);
                    }}
                  >
                    Descuento solicitado{t.pendingDiscountCount > 1 ? ` (${t.pendingDiscountCount})` : ""}
                  </button>
                ) : null}

                {/* Solo cuando el cliente pidió pagar en el POS ("Pidió pagar en el POS"): hace lo
                    mismo que "Cobrar en terminal" del detalle de la mesa. */}
                {t.posPaymentRequest ? (
                  <button
                    className="cds-x-btn cds-x-btn-pay"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onChargePosPayment?.(t);
                    }}
                  >
                    Solicitud de pago{(t.posPaymentRequests || []).length > 1 ? ` (${t.posPaymentRequests.length})` : ""}
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── modales ── */}
      <RfmHistoryModal
        open={showHistory}
        onClose={() => setShowHistory(false)}
        table={activeTable}
        events={activeTable?.events}
        onChargePosPayment={onChargePosPayment}
        onReviewDiscounts={(table) => {
          setShowHistory(false);
          onReviewDiscounts?.(table);
        }}
      />
    </div>
  );
}